import React, { useMemo } from "react";
import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  PaperAirplaneIcon,
  LockClosedIcon,
  ArrowPathIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { classNames, formatDateTime, formatRelative } from "./utils";
import { SafeHtml, htmlToPreview, looksLikeHtml } from "../../../utils/sanitizeHtml";

// Build a single chronological list of thread events out of the pieces we
// already get back from `/inquiries/:id`:
//
//   - The original inquiry (synthesized as the first inbound message if it
//     isn't already present in `messages[]`).
//   - `inquiry.messages[]` — the full Gmail thread (inbound + outbound),
//     the canonical source of conversation content.
//   - `inquiry.responses[]` — used ONLY for outbound team replies that
//     never made it into `messages[]` (e.g. the legacy "Mark replied"
//     button or the reply-without-Gmail path). Inbound entries in
//     `responses[]` are duplicates of inbound messages, so we drop them
//     to avoid double display.
//   - Lightweight status events (closed/replied/reopened) so operators
//     can follow the lifecycle inline.
//
// Events are sorted oldest -> newest so reading top-down reads like a real
// conversation. The modal auto-scrolls to the bottom on open.
export function buildThreadEvents(inquiry) {
  if (!inquiry) return [];

  const events = [];
  const seenMessageIds = new Set();

  // Timestamp helpers — tolerate mixed ISO strings, Date objects, numbers.
  const toMs = (v) => {
    if (!v) return null;
    const t = v instanceof Date ? v.getTime() : new Date(v).getTime();
    return Number.isFinite(t) ? t : null;
  };

  const createdAtMs = toMs(inquiry.createdAt) ?? Date.now();

  // 1) Messages from Gmail thread (most reliable).
  const messages = Array.isArray(inquiry.messages) ? inquiry.messages : [];
  const hasMessages = messages.length > 0;
  for (const m of messages) {
    if (m?.messageId) seenMessageIds.add(String(m.messageId));
    const when = toMs(m?.timestamp) ?? createdAtMs;
    events.push({
      kind: "message",
      key: `msg:${m?.messageId || when}:${m?.direction}`,
      direction: m?.direction === "outbound" ? "outbound" : "inbound",
      at: when,
      from: Array.isArray(m?.from) ? m.from.join(", ") : m?.from || "",
      to: Array.isArray(m?.to) ? m.to.join(", ") : m?.to || "",
      subject: m?.subject || "",
      html: m?.html || "",
      text: m?.body || "",
    });
  }

  // 2) Synthesize the opening inquiry if it isn't already reflected in
  //    messages. Form submissions (source="guest-form") typically skip
  //    Gmail entirely and only live on the top-level `message`/`subject`
  //    fields of the inquiry — without this synthesis the modal opens
  //    with an "empty thread" which is the most common complaint.
  const hasOpenerInMessages = messages.some(
    (m) => m?.direction === "inbound" && toMs(m?.timestamp) && Math.abs(toMs(m.timestamp) - createdAtMs) < 60_000
  );
  if (!hasOpenerInMessages && (inquiry.message || inquiry.subject)) {
    events.push({
      kind: "message",
      key: `opener:${inquiry._id}`,
      direction: "inbound",
      at: createdAtMs,
      from: inquiry.email ? `${inquiry.name || "Guest"} <${inquiry.email}>` : inquiry.name || "Guest",
      to: "Bellhopt Support",
      subject: inquiry.subject || "New inquiry",
      html: "",
      text: inquiry.message || "",
      isOpener: true,
      channelHint: inquiry.source || "web-form",
    });
  }

  // 3) Responses that are NOT already represented in messages.
  //    - Inbound responses are written by crmReplySync every time a guest
  //      reply comes in, but those inbound entries are also in messages[]
  //      (de-duped above). To be safe, drop any response whose message
  //      text is already present in any captured message.
  //    - Outbound responses written by the legacy "reply without email"
  //      path have no messageId and wouldn't appear in messages at all —
  //      surface them as team events so the history is complete.
  const responses = Array.isArray(inquiry.responses) ? inquiry.responses : [];
  const messageTextPool = new Set(
    messages.map((m) => (m?.body || "").trim()).filter(Boolean)
  );
  for (const r of responses) {
    if (!r) continue;
    const trimmed = (r.message || "").trim();
    if (trimmed && messageTextPool.has(trimmed)) continue;
    // Guess direction: responder matches an inbound sender name/email?
    const isInbound = Boolean(
      (inquiry.email && r.responder && r.responder.toLowerCase() === inquiry.email.toLowerCase()) ||
        (inquiry.name && r.responder && r.responder.toLowerCase() === inquiry.name.toLowerCase())
    );
    // Only add team-side responses that aren't duplicates; drop inbound
    // duplicates (handled by messages).
    if (isInbound && hasMessages) continue;
    events.push({
      kind: "response",
      key: `resp:${r.respondedAt || r.message || Math.random()}`,
      direction: isInbound ? "inbound" : "outbound",
      at: toMs(r.respondedAt) ?? Date.now(),
      from: r.responder || (isInbound ? inquiry.name || "Guest" : "Bellhopt Support"),
      to: isInbound ? "Bellhopt Support" : inquiry.email || "",
      subject: "",
      html: looksLikeHtml(r.message) ? r.message : "",
      text: looksLikeHtml(r.message) ? "" : r.message || "",
      templateUsed: r.templateUsed || "",
    });
  }

  // 4) Lifecycle events — lightweight pills inside the timeline.
  if (inquiry.replied && inquiry.respondedAt) {
    events.push({
      kind: "system",
      key: `sys:replied:${inquiry.respondedAt}`,
      at: toMs(inquiry.respondedAt) ?? Date.now(),
      label: "Marked as replied",
      tone: "indigo",
    });
  }
  if (inquiry.closed && inquiry.updatedAt) {
    events.push({
      kind: "system",
      key: `sys:closed:${inquiry.updatedAt}`,
      at: toMs(inquiry.updatedAt) ?? Date.now(),
      label: "Inquiry closed",
      tone: "zinc",
    });
  }

  events.sort((a, b) => (a.at || 0) - (b.at || 0));
  return events;
}

function Avatar({ name, inbound }) {
  const initials = (name || "?")
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("") || "?";
  return (
    <div
      className={classNames(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ring-1",
        inbound
          ? "bg-amber-50 text-amber-800 ring-amber-200"
          : "bg-indigo-50 text-indigo-700 ring-indigo-200"
      )}
      aria-hidden
    >
      {initials}
    </div>
  );
}

function DirectionTag({ inbound }) {
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1",
        inbound
          ? "bg-amber-50 text-amber-800 ring-amber-200"
          : "bg-indigo-50 text-indigo-700 ring-indigo-200"
      )}
    >
      {inbound ? (
        <ArrowDownLeftIcon className="h-3 w-3" />
      ) : (
        <ArrowUpRightIcon className="h-3 w-3" />
      )}
      {inbound ? "Guest" : "Team"}
    </span>
  );
}

function MessageBubble({ event }) {
  const inbound = event.direction === "inbound";
  const bodyHtml = event.html || event.text;
  // Very long messages (e.g. forwarded quoted threads) collapse by default
  // so newer replies aren't lost under walls of text. Opener and last-reply
  // are always expanded for immediate context.
  const preview = htmlToPreview(event.html || event.text || "", { maxLength: 600 });
  const longBody = preview.length >= 520 && !event.isOpener;
  const [expanded, setExpanded] = React.useState(!longBody);
  return (
    <article
      className={classNames(
        "grid gap-2",
        inbound ? "grid-cols-[auto_1fr]" : "grid-cols-[1fr_auto] justify-items-end"
      )}
    >
      {inbound ? <Avatar name={event.from} inbound /> : null}
      <div
        className={classNames(
          "min-w-0 max-w-[85%] rounded-2xl border px-3.5 py-2.5 shadow-sm",
          inbound
            ? "border-amber-100 bg-amber-50/50 text-zinc-900"
            : "border-indigo-100 bg-indigo-50/50 text-zinc-900"
        )}
      >
        <header className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-zinc-600">
          <DirectionTag inbound={inbound} />
          <span className="truncate font-semibold text-zinc-800" title={event.from}>
            {event.from || (inbound ? "Guest" : "Bellhopt")}
          </span>
          {event.subject ? (
            <>
              <span className="text-zinc-300">·</span>
              <span className="truncate" title={event.subject}>
                {event.subject}
              </span>
            </>
          ) : null}
          <span className="text-zinc-300">·</span>
          <time dateTime={new Date(event.at).toISOString()} title={formatDateTime(event.at)}>
            {formatRelative(event.at)}
          </time>
          {event.isOpener ? (
            <>
              <span className="text-zinc-300">·</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-white px-1.5 py-0.5 text-[10px] font-semibold text-zinc-600 ring-1 ring-zinc-200">
                <SparklesIcon className="h-3 w-3" />
                Original message
              </span>
            </>
          ) : null}
          {event.templateUsed ? (
            <>
              <span className="text-zinc-300">·</span>
              <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-medium text-zinc-600 ring-1 ring-zinc-200">
                Template
              </span>
            </>
          ) : null}
        </header>

        <div className={classNames(!expanded ? "line-clamp-6" : "")}>
          {bodyHtml ? (
            <SafeHtml
              value={bodyHtml}
              className="prose prose-sm max-w-none text-sm leading-relaxed text-zinc-800 prose-a:text-indigo-600 prose-a:underline prose-p:my-1.5"
            />
          ) : (
            <p className="text-sm italic text-zinc-400">(empty message)</p>
          )}
        </div>

        {longBody ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1 text-[11px] font-semibold text-indigo-600 hover:underline"
          >
            {expanded ? "Show less" : "Show full message"}
          </button>
        ) : null}
      </div>
      {!inbound ? <Avatar name={event.from} inbound={false} /> : null}
    </article>
  );
}

function SystemEventRow({ event }) {
  const tone = event.tone || "zinc";
  const toneMap = {
    indigo: "bg-indigo-50 text-indigo-700 ring-indigo-200",
    zinc: "bg-zinc-100 text-zinc-700 ring-zinc-200",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  };
  const Icon = event.label?.toLowerCase().includes("closed")
    ? LockClosedIcon
    : event.label?.toLowerCase().includes("reopen")
    ? ArrowPathIcon
    : PaperAirplaneIcon;
  return (
    <div className="flex items-center gap-2 py-1">
      <span className="h-px flex-1 bg-zinc-200" />
      <span
        className={classNames(
          "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1",
          toneMap[tone]
        )}
      >
        <Icon className="h-3 w-3" />
        {event.label}
        <span className="ml-1 normal-case text-[10px] font-medium text-zinc-500">
          {formatRelative(event.at)}
        </span>
      </span>
      <span className="h-px flex-1 bg-zinc-200" />
    </div>
  );
}

export default function ConversationThread({ inquiry, emptyHint }) {
  const events = useMemo(() => buildThreadEvents(inquiry), [inquiry]);
  const endRef = React.useRef(null);

  React.useEffect(() => {
    // Keep the latest message in view when the modal opens or a new reply
    // lands. `requestAnimationFrame` lets the DOM finish laying out first.
    if (!endRef.current) return;
    const id = requestAnimationFrame(() => {
      endRef.current?.scrollIntoView({ block: "end", behavior: "auto" });
    });
    return () => cancelAnimationFrame(id);
  }, [events.length, inquiry?._id]);

  if (!events.length) {
    return (
      <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-zinc-200 bg-white p-10 text-center text-sm text-zinc-500">
        {emptyHint || "No messages on this thread yet."}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {events.map((event) =>
        event.kind === "system" ? (
          <SystemEventRow key={event.key} event={event} />
        ) : (
          <MessageBubble key={event.key} event={event} />
        )
      )}
      <div ref={endRef} />
    </div>
  );
}
